import React, { createContext, useContext, useCallback, ReactNode } from 'react'
import { useModals } from 'almanac-ui/Modal'
import { UpdateModal } from 'components/UpdateModal/UpdateModal'
import { useCreateUpdateModalView } from 'hooks/queries/updateModals/useCreateUpdateModalView'
import { useUpdateModals, UpdateModal as UpdateModalType } from 'hooks/queries/updateModals/useUpdateModals'
import { useUpdateModalsWebsocket } from './useUpdateModalsWebsocket'

export const UpdatesContext = createContext({
  pendingUpdates: [] as UpdateModalType[],
  displayLatestUpdate: () => {},
  markModalAsViewed: (modalId: number) => {},
})

export const UpdatesProvider = ({ children }: { children: ReactNode }) => {
  const { data: updateModals } = useUpdateModals()
  const { openModal } = useModals()
  const { mutate: createUpdateModalView } = useCreateUpdateModalView()

  useUpdateModalsWebsocket()

  const markModalAsViewed = useCallback(
    (modalId: number) => {
      createUpdateModalView({ update_modal_id: modalId })
    },
    [createUpdateModalView],
  )

  const displayModal = useCallback(
    (modal: UpdateModalType) => {
      openModal(UpdateModal, { updateModal: modal })
    },
    [openModal],
  )

  const displayLatestUpdate = useCallback(() => {
    if (updateModals && updateModals.length > 0) {
      const sortedModals = [...updateModals].sort((a, b) => b.id - a.id)
      const newestModal = sortedModals[0]
      displayModal(newestModal)
      sortedModals.forEach((modal) => markModalAsViewed(modal.id))
    }
  }, [updateModals, displayModal, markModalAsViewed])

  const value = {
    pendingUpdates: [...(updateModals ?? [])].sort((a, b) => b.id - a.id),
    displayLatestUpdate,
    markModalAsViewed,
  }

  return <UpdatesContext.Provider value={value}>{children}</UpdatesContext.Provider>
}

export const useUpdates = () => {
  const context = useContext(UpdatesContext)
  if (context === undefined) {
    throw new Error('useUpdates must be used within an UpdatesProvider')
  }
  return context
}

export const usePendingUpdates = () => {
  const { pendingUpdates } = useUpdates()
  return pendingUpdates || []
}
